import axios from "axios";
import * as XLSX from "xlsx";

const API_URL = "https://backend-proxy.yros.io"
//const API_URL = "http://127.0.0.1:5003"
const API_BASE_URL = API_URL + "/proxy/api";

const defaultHeaders = {
    Accept: "application/json",
    "Content-Type": "application/json",
};

class ApiService {
    constructor() {
        this.axiosInstance = axios.create({
            baseURL: API_BASE_URL,
            headers: defaultHeaders,
        });

        this.axiosInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem("access_token");
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        this.axiosInstance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (error.response && error.response.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;
                    try {
                        const newToken = await this.refreshToken();
                        localStorage.setItem("access_token", newToken);
                        this.axiosInstance.defaults.headers.Authorization = `Bearer ${newToken}`;
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return this.axiosInstance(originalRequest);
                    } catch (refreshError) {
                        console.error("Session expirée. Déconnexion...");
                        this.logoutUser();
                        return Promise.reject(refreshError);
                    }
                }
                return Promise.reject(error);
            }
        );
    }

    async refreshToken() {
        const refreshToken = localStorage.getItem("refresh_token");
        if (!refreshToken) {
            throw new Error("Aucun refresh token disponible");
        }

        const response = await axios.post(`${API_BASE_URL}/refresh`, {}, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${refreshToken}`,
            },
        });

        return response.data.access_token;
    }

    logoutUser() {
        localStorage.removeItem("companyName");
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem('isConnected');
        window.location.href = "/login";
    }

    async authenticate(email, password) {
        return axios.post(API_URL + "/login", {
            login: email,
            password: password,
        }, {
            headers: defaultHeaders,
        });
    }

    async register(data) {
        return axios.post(API_URL + "/public/api/create_user_company", { data: data }, {
            headers: defaultHeaders,
        });
    }

    async getBlobFile(endpoint) {
        return this.axiosInstance.get(endpoint, {
            responseType: "blob",
        });
    }

    async getElement(endpoint) {
        return this.axiosInstance.get(endpoint, { headers: defaultHeaders });
    }

    async getElementById(endpoint, id) {
        return this.axiosInstance.get(`${endpoint}/${id}`, { headers: defaultHeaders });
    }

    async saveElement(endpoint, data, sendFile = false) {
        const headers = sendFile
            ? { ...defaultHeaders, "Content-Type": "multipart/form-data" }
            : defaultHeaders;

        return this.axiosInstance.post(endpoint, sendFile ? data : JSON.stringify(data), { headers });
    }

    async searchElement(endpoint, data) {
        return this.axiosInstance.post(endpoint, JSON.stringify(data), { headers: defaultHeaders });
    }

    async searchFile(endpoint, data) {
        return this.axiosInstance.post(endpoint, JSON.stringify(data), {
            headers: defaultHeaders,
            responseType: "blob",
        });
    }

    async updateElementById(endpoint, id, data, sendFile = false) {
        const headers = sendFile
            ? { ...defaultHeaders, "Content-Type": "multipart/form-data" }
            : defaultHeaders;

        return this.axiosInstance.put(`${endpoint}/${id}/`, sendFile ? data : JSON.stringify(data), { headers });
    }

    async changeElementById(endpoint, id, data, sendFile = false) {
        const headers = sendFile
            ? { ...defaultHeaders, "Content-Type": "multipart/form-data" }
            : defaultHeaders;

        return this.axiosInstance.patch(`${endpoint}/${id}/`, sendFile ? data : JSON.stringify(data), { headers });
    }

    async deleteElement(endpoint, id) {
        return this.axiosInstance.delete(`${endpoint}/${id}/`, { headers: defaultHeaders });
    }

    async prepareImportFile(file) {
        if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName], { FS: ';' });
            return new Blob([csv], { type: 'text/csv' });
        } else if (file.name.endsWith('.csv')) {
            const text = await file.text();
            const firstLine = text.split('\n')[0];
            if (firstLine.includes(';')) {
                return new Blob([text], { type: 'text/csv' });
            } else if (firstLine.includes(',')) {
                const csv = text.replace(/,/g, ';');
                return new Blob([csv], { type: 'text/csv' });
            } else {
                throw new Error('Le séparateur du CSV doit être ";" ou ",".');
            }
        } else {
            throw new Error('Format de fichier non supporté.');
        }
    }

    async exportData({ model_name, fields, domain = [], format = "csv", filename = "export" }) {
        const response = await this.searchElement('/export', {
            model_name,
            fields,
            domain
        });
        const data = response.data.result.data || [];

       if (format === "csv") {
            const csvRows = [];
            csvRows.push(fields.join(';'));
            data.forEach(row => {
                csvRows.push(
                    fields.map(f => {
                        if ((f === "employee_id" || f === "absence_type_id") && row[f][1]) {
                            return row[f][1];
                        }
                        const val = row[f];
                        return (val === false || val === undefined || val === null) ? "" : val;
                    }).join(';')
                );
            });
            const blob = new Blob([csvRows.join('\r\n')], { type: "text/csv" });
            this.downloadBlob(blob, filename + ".csv");
        } else if (format === "xlsx") {
            const XLSX = await import("xlsx");
            const cleanData = data.map(row =>
                Object.fromEntries(
                    fields.map(f => [
                        f,
                        ((f === "employee_id" || f === "absence_type_id" )&& row.employee_id && Array.isArray(row[f]) && row[f][1])
                            ? row[f][1]
                            : ((row[f] === false || row[f] === undefined || row[f] === null) ? "" : row[f])
                    ])
                )
            );
            const ws = XLSX.utils.json_to_sheet(cleanData, { header: fields });
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Export");
            const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            this.downloadBlob(blob, filename + ".xlsx");
        } else {
            throw new Error("Format non supporté");
        }
    }

    downloadBlob(blob, filename) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    }

    async sendEmail({ to, subject, html, template_id, params, attachment }) {
        const response = await fetch('http://127.0.0.1:5003/api/send-email', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ to, subject, html, template_id, params, attachment })
        });
        return response.json();
    }
}

export const apiService = new ApiService();